<script>
import Layout from '../../layouts/main'
import PageHeader from '@/components/page-header'
import appConfig from '@/app.config'
import Fixtures from '@/services/Fixtures'
import Liveblog from '@/services/Liveblog'
import Swal from 'sweetalert2'
import modalUpdateMatchFromLiveblog from '@/components/modals/liveblog/modalUpdateMatchFromLiveblog'
import modalPushNotifLiveblog from '@/components/modals/liveblog/modalPushNotifLiveblog'
import liveblogTimeline from '@/components/liveblog/liveblogTimeline.vue'
import liveblogComposer from '@/components/liveblog/liveblogComposer.vue'
import { mapEventToLiveblogForm } from '@/components/modals/liveblog/liveblogEventPrefill'
import { hasUsableLineups } from '@/components/modals/liveblog/liveblogFormOptions'
import { parseLiveblogPayload } from '@/components/liveblog/liveblogMediaUtils'
export default {
    components: {
        Layout,
        PageHeader,
        modalUpdateMatchFromLiveblog,
        modalPushNotifLiveblog,
        liveblogTimeline,
        liveblogComposer,
    },
    page: {
        title: 'Liveblog',
        meta: [
            {
                name: 'description',
                content: appConfig.description,
            },
        ],
    },
    data() {
        return {
            title: 'Liveblog',
            items: [
                { text: 'Content' },
                { text: 'Liveblog', active: true },
            ],
            isBusy: false,
            reorderBusy: false,
            editingEventId: '',
            showImportSection: false,
            eventsBusy: false,
            matches: [],
            selectedMatchId: '',
            selectedMatch: null,
            matchEvents: [],
            tableData: [],
            orderDirection: 'DESC',
            eventsFilter: null,
            eventFields: [
                { key: 'event_minute', label: 'Minute', sortable: true },
                { key: 'event_type_name', label: 'Event', sortable: true },
                { key: 'player_name', label: 'Player', sortable: true },
                { key: 'score', label: 'Score' },
                { key: 'place', label: 'Side', sortable: true },
                { key: 'import_status', label: 'Status' },
                'action',
            ],
            eventTypes: [],
            eventReasons: [],
            periods: [],
            matchLineups: null,
            timelineMode: 'events',
            timelineModeLoading: false,
            timelineModeBusy: false,
            timelineModeHydrating: false,
            updateMatchEnabled: false,
            pushNotifEnabled: false,
            filter: null,
        }
    },
    middleware: 'authentication',
    computed: {
        matchOptions() {
            return this.matches.map((m) => ({
                value: m.match_id,
                text: `${m.home_team_code} vs ${m.away_team_code} — ${m.date_time || ''} (${m.match_status || ''})`,
            }))
        },
        importedEventIds() {
            const ids = new Set()
            this.tableData.forEach((entry) => {
                if (entry.event_id) ids.add(String(entry.event_id))
                if (entry.provider_event_id) ids.add(String(entry.provider_event_id))
            })
            return ids
        },
        notImportedEventsCount() {
            return this.matchEvents.filter((e) => !this.importedEventIds.has(e.event_id)).length
        },
        timelineModeShowLoader() {
            return this.timelineModeLoading || this.timelineModeBusy
        },
        canReorderLiveblog() {
            return Boolean(this.selectedMatchId && this.tableData.length > 1 && !this.filter)
        },
    },
    async created() {
        await Promise.all([this.loadMatches(), this.loadMetadata()])
    },
    methods: {
        async loadMatches() {
            try {
                const response = await Fixtures.getCurrentMatches()
                this.matches = response.data.matches || []
            } catch (error) {
                this.matches = []
                this.failedmsg(error.response?.data?.error || 'Failed to load matches')
            }
        },
        normalizeApiList(response, listKey) {
            const data = response?.data
            if (!data) return []
            if (listKey && Array.isArray(data[listKey])) return data[listKey]
            if (Array.isArray(data)) return data
            return []
        },
        async loadPeriods() {
            try {
                let response = await Liveblog.getMatchPeriods({ period_sport: 'football' })
                let list = this.normalizeApiList(response, 'periods')
                if (!list.length) {
                    response = await Liveblog.getMatchPeriods()
                    list = this.normalizeApiList(response, 'periods')
                }
                this.periods = list.sort((a, b) => Number(a.period_id) - Number(b.period_id))
            } catch (error) {
                this.periods = []
            }
            return this.periods
        },
        async loadEventTypes() {
            try {
                const response = await Liveblog.getEventTypes()
                const list = this.normalizeApiList(response, 'event_types')
                this.eventTypes = list.sort((a, b) => Number(a.event_type_id) - Number(b.event_type_id))
            } catch (error) {
                this.eventTypes = []
            }
            return this.eventTypes
        },
        async loadMetadata() {
            await Promise.all([
                this.loadEventTypes(),
                Liveblog.getEventReasons()
                    .then((res) => {
                        this.eventReasons = this.normalizeApiList(res, 'event_reasons')
                    })
                    .catch(() => {
                        this.eventReasons = []
                    }),
                this.loadPeriods(),
            ])
        },
        onMatchChange() {
            this.selectedMatch = this.matches.find((m) => m.match_id === this.selectedMatchId) || null
            this.editingEventId = ''
            if (this.selectedMatchId) {
                this.loadTimelineMode()
                this.loadMatchData()
                this.$nextTick(() => {
                    if (this.$refs.composer) {
                        this.$refs.composer.startAdd('event')
                    }
                })
            } else {
                this.timelineMode = 'events'
                this.updateMatchEnabled = false
                this.pushNotifEnabled = false
                this.timelineModeLoading = false
                this.tableData = []
                this.matchEvents = []
                this.matchLineups = null
            }
        },
        applyTimelineSettings(payload) {
            const mode = String(payload?.timeline_mode ?? 'events').toLowerCase()
            this.timelineModeHydrating = true
            this.timelineMode = mode === 'liveblog' ? 'liveblog' : 'events'
            this.updateMatchEnabled = Number(payload?.update_match) === 1
            this.pushNotifEnabled = Number(payload?.push_notif) === 1
            this.$nextTick(() => {
                this.timelineModeHydrating = false
            })
        },
        async loadTimelineMode() {
            if (!this.selectedMatchId) {
                this.timelineMode = 'events'
                this.updateMatchEnabled = false
                this.pushNotifEnabled = false
                this.timelineModeLoading = false
                return
            }
            this.timelineModeLoading = true
            await this.$nextTick()
            try {
                const response = await Liveblog.getTimelineMode(this.selectedMatchId)
                this.applyTimelineSettings(response.data)
            } catch (error) {
                this.timelineMode = 'events'
                this.updateMatchEnabled = false
                this.pushNotifEnabled = false
            } finally {
                this.timelineModeLoading = false
            }
        },
        openUpdateMatchModal() {
            if (!this.selectedMatchId || this.timelineModeShowLoader) {
                return
            }
            this.$refs.modalUpdateMatchFromLiveblog.open()
        },
        onUpdateMatchCheckboxChange(nextChecked) {
            if (!this.selectedMatchId || this.timelineModeShowLoader) {
                return
            }
            if (Boolean(nextChecked) === Boolean(this.updateMatchEnabled)) {
                return
            }
            // Keep checkbox controlled by `updateMatchEnabled`.
            // Only change after the confirmation modal succeeds.
            this.openUpdateMatchModal()
        },
        onUpdateMatchSettingSaved(payload) {
            this.updateMatchEnabled = Number(payload?.update_match) === 1
            const label = this.updateMatchEnabled ? 'enabled' : 'disabled'
            this.successmsg(`Match update from liveblog ${label}`)
            this.loadTimelineMode()
        },
        onUpdateMatchSettingError(message) {
            this.failedmsg(message)
        },
        openPushNotifModal() {
            if (!this.selectedMatchId || this.timelineModeShowLoader) {
                return
            }
            this.$refs.modalPushNotifLiveblog.open()
        },
        onPushNotifCheckboxChange(nextChecked) {
            if (!this.selectedMatchId || this.timelineModeShowLoader) {
                return
            }
            if (Boolean(nextChecked) === Boolean(this.pushNotifEnabled)) {
                return
            }
            this.openPushNotifModal()
        },
        onPushNotifSettingSaved(payload) {
            this.pushNotifEnabled = Number(payload?.push_notif) === 1
            const label = this.pushNotifEnabled ? 'enabled' : 'disabled'
            this.successmsg(`Push notifications from liveblog ${label}`)
            this.loadTimelineMode()
        },
        onPushNotifSettingError(message) {
            this.failedmsg(message)
        },
        setTimelineModeOption(mode) {
            if (
                !this.selectedMatchId
                || this.timelineModeShowLoader
                || this.timelineModeHydrating
            ) {
                return
            }
            const next = mode === 'liveblog' ? 'liveblog' : 'events'
            if (next === this.timelineMode) return
            this.timelineMode = next
            this.onTimelineModeChange()
        },
        async onTimelineModeChange() {
            if (
                !this.selectedMatchId
                || this.timelineModeBusy
                || this.timelineModeLoading
                || this.timelineModeHydrating
            ) {
                return
            }
            const next = this.timelineMode === 'liveblog' ? 'liveblog' : 'events'

            this.timelineModeBusy = true
            await this.$nextTick()
            try {
                await Liveblog.setTimelineMode(this.selectedMatchId, next)
                const response = await Liveblog.getTimelineMode(this.selectedMatchId)
                this.applyTimelineSettings(response.data)
                const label = this.timelineMode === 'liveblog' ? 'Liveblog' : 'Sport events'
                this.successmsg(`App timeline set to ${label}`)
            } catch (error) {
                this.failedmsg(error.response?.data?.error || 'Failed to update timeline mode')
                await this.loadTimelineMode()
            } finally {
                this.timelineModeBusy = false
            }
        },
        async loadMatchData() {
            await Promise.all([this.getMatchEvents(), this.loadMatchExtendedLineups(), this.getLiveblog()])
        },
        normalizeLineupsPayload(lineups) {
            if (!lineups || Array.isArray(lineups) || typeof lineups !== 'object') {
                return null
            }
            return lineups
        },
        async loadSquadFallbackLineups() {
            const match = this.selectedMatch
            if (!match) return null
            const homeTeamId = match.home_team_id
            const awayTeamId = match.away_team_id
            if (!homeTeamId && !awayTeamId) return null

            try {
                const [homeRes, awayRes] = await Promise.all([
                    homeTeamId ? Fixtures.getSportSquadRaw(homeTeamId) : Promise.resolve(null),
                    awayTeamId ? Fixtures.getSportSquadRaw(awayTeamId) : Promise.resolve(null),
                ])
                const homeSquad = homeRes?.data?.squad || []
                const awaySquad = awayRes?.data?.squad || []
                if (!homeSquad.length && !awaySquad.length) return null
                return {
                    home_team_starters: homeSquad,
                    away_team_starters: awaySquad,
                    home_team_subs: [],
                    away_team_subs: [],
                }
            } catch (error) {
                return null
            }
        },
        async loadMatchExtendedLineups() {
            if (!this.selectedMatchId) return
            try {
                const response = await Fixtures.getSportMatchExtended(this.selectedMatchId)
                let lineups = this.normalizeLineupsPayload(response.data?.lineups)
                if (!hasUsableLineups(lineups)) {
                    lineups = await this.loadSquadFallbackLineups()
                }
                this.matchLineups = lineups
            } catch (error) {
                this.matchLineups = await this.loadSquadFallbackLineups()
            }
        },
        async getMatchEvents() {
            if (!this.selectedMatchId) return
            try {
                this.eventsBusy = true
                const response = await Fixtures.getSportMatchEvents(this.selectedMatchId)
                this.matchEvents = response.data.events || []
            } catch (error) {
                this.matchEvents = []
                this.failedmsg(error.response?.data?.error || 'Failed to load match events')
            } finally {
                this.eventsBusy = false
            }
        },
        isEventImported(eventId) {
            return this.importedEventIds.has(eventId)
        },
        async addLiveblogFromEvent(event) {
            if (!this.selectedMatchId || !event) return
            await Promise.all([
                this.loadEventTypes(),
                this.loadPeriods(),
                !this.matchLineups ? this.loadMatchExtendedLineups() : Promise.resolve(),
            ])
            const prefill = mapEventToLiveblogForm(event, this.selectedMatch)
            this.editingEventId = ''
            if (this.$refs.composer) {
                this.$refs.composer.loadPrefill(prefill)
            }
            this.showImportSection = true
        },
        normalizeLiveblogEntry(entry) {
            if (!entry || typeof entry !== 'object') return entry
            const payload = parseLiveblogPayload(entry.payload)
            return {
                ...entry,
                payload: payload || entry.payload || null,
            }
        },
        async getLiveblog() {
            if (!this.selectedMatchId) return
            try {
                this.toggleBusy()
                const response = await Liveblog.getLiveblog(this.selectedMatchId, {
                    order: this.orderDirection,
                })
                this.tableData = (response.data.liveblog || []).map((entry) => this.normalizeLiveblogEntry(entry))
                this.toggleBusy()
                await this.refreshSelectedMatch()
            } catch (error) {
                this.toggleBusy()
                this.tableData = []
                const err = error.response?.data?.error
                if (err && err !== 'No liveblog data') {
                    this.failedmsg(err)
                }
            }
        },
        async refreshSelectedMatch() {
            if (!this.selectedMatchId) return
            try {
                const response = await Fixtures.getSportMatch(this.selectedMatchId)
                const match = response.data?.match || response.data
                if (match) {
                    this.selectedMatch = { ...this.selectedMatch, ...match }
                    const idx = this.matches.findIndex((m) => m.match_id === this.selectedMatchId)
                    if (idx >= 0) {
                        this.$set(this.matches, idx, { ...this.matches[idx], ...match })
                    }
                }
            } catch (error) {
                // non-fatal
            }
        },
        async onTimelineReorder({ eventId, targetOrder }) {
            if (!this.selectedMatchId || !eventId) return
            this.reorderBusy = true
            try {
                await Liveblog.reorderLiveblog(this.selectedMatchId, eventId, targetOrder)
                await this.getLiveblog()
                this.successmsg('Order updated')
            } catch (error) {
                this.failedmsg(error.response?.data?.error || 'Failed to reorder entry')
                await this.getLiveblog()
            } finally {
                this.reorderBusy = false
            }
        },
        onTimelineReorderCancel() {
            this.getLiveblog()
        },
        onEditEntry(item) {
            this.editingEventId = item.event_id
            if (this.$refs.composer) {
                this.$refs.composer.loadForEdit(item)
            }
        },
        onComposerSaved() {
            this.editingEventId = ''
            this.loadMatchData()
        },
        toggleOrder() {
            this.orderDirection = this.orderDirection === 'DESC' ? 'ASC' : 'DESC'
            this.getLiveblog()
        },
        removeLiveblog(event_id) {
            Swal.fire({
                title: 'Remove liveblog entry?',
                text: 'This entry will be permanently deleted.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#34c38f',
                cancelButtonColor: '#f46a6a',
                confirmButtonText: 'Remove',
            }).then(async (result) => {
                if (result.value) {
                    try {
                        const response = await Liveblog.deleteLiveblog(this.selectedMatchId, event_id)
                        if (response.data) {
                            if (this.editingEventId === event_id) {
                                this.editingEventId = ''
                                if (this.$refs.composer) this.$refs.composer.resetAdd()
                            }
                            await this.loadMatchData()
                            this.successmsg('Removed', 'Liveblog entry deleted successfully')
                        } else {
                            this.failedmsg(response.data?.error || 'Failed')
                        }
                    } catch (error) {
                        this.failedmsg(error.response?.data?.error || 'An error occurred')
                    }
                }
            })
        },
    },
}
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div class="row mb-3">
            <div class="col-12">
                <div class="card">
                    <div class="card-body py-3">
                        <div class="row align-items-center g-3">
                            <div class="col-md-7">
                                <label class="form-label">Match</label>
                                <b-form-select
                                    v-model="selectedMatchId"
                                    :options="[{ value: '', text: 'Select a match...' }, ...matchOptions]"
                                    @change="onMatchChange"
                                ></b-form-select>
                                <div v-if="selectedMatch" class="mt-2 text-muted small">
                                    {{ selectedMatch.home_team_code }} {{ selectedMatch.score_home }} -
                                    {{ selectedMatch.score_away }} {{ selectedMatch.away_team_code }}
                                    · {{ selectedMatch.competition_name }}
                                </div>
                            </div>
                            <div class="col-md-5 d-flex flex-column justify-content-center align-items-center text-center">
                                <label class="form-label mb-2">App match timeline</label>
                                <div
                                    class="timeline-settings-panel position-relative w-100"
                                    :class="{ 'timeline-settings-panel--with-checkboxes': timelineMode === 'liveblog' }"
                                >
                                    <div
                                        v-show="timelineModeShowLoader"
                                        class="timeline-mode-loader"
                                        aria-live="polite"
                                        aria-busy="true"
                                    >
                                        <b-spinner variant="primary" class="align-middle"></b-spinner>
                                        <span class="timeline-mode-loader-text">Loading…</span>
                                    </div>
                                    <div
                                        class="timeline-settings-panel-content"
                                        :class="{ 'timeline-settings-panel-content--dimmed': timelineModeShowLoader }"
                                    >
                                        <div class="timeline-mode-control">
                                            <div class="timeline-mode-buttons">
                                                <button
                                                    type="button"
                                                    class="btn btn-sm timeline-mode-btn"
                                                    :class="timelineMode === 'events' ? 'btn-primary' : 'btn-outline-primary'"
                                                    :disabled="!selectedMatchId || timelineModeShowLoader"
                                                    @click="setTimelineModeOption('events')"
                                                >
                                                    Sport events
                                                </button>
                                                <button
                                                    type="button"
                                                    class="btn btn-sm timeline-mode-btn timeline-mode-btn--second"
                                                    :class="timelineMode === 'liveblog' ? 'btn-primary' : 'btn-outline-primary'"
                                                    :disabled="!selectedMatchId || timelineModeShowLoader"
                                                    @click="setTimelineModeOption('liveblog')"
                                                >
                                                    Liveblog
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            v-if="timelineMode === 'liveblog'"
                                            class="mt-3 w-100 d-flex justify-content-center"
                                        >
                                            <div class="timeline-options-checkboxes">
                                                <b-form-checkbox
                                                    switch
                                                    :checked="updateMatchEnabled"
                                                    :disabled="!selectedMatchId || timelineModeShowLoader"
                                                    class="timeline-option-checkbox mb-0"
                                                    @change="onUpdateMatchCheckboxChange"
                                                >
                                                    Update match from liveblog
                                                </b-form-checkbox>
                                                <b-form-checkbox
                                                    switch
                                                    :checked="pushNotifEnabled"
                                                    :disabled="!selectedMatchId || timelineModeShowLoader"
                                                    class="timeline-option-checkbox mb-0"
                                                    @change="onPushNotifCheckboxChange"
                                                >
                                                    Push notifications from Liveblog
                                                </b-form-checkbox>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <modalUpdateMatchFromLiveblog
            ref="modalUpdateMatchFromLiveblog"
            :match-id="selectedMatchId"
            :match="selectedMatch"
            :update-match-enabled="updateMatchEnabled"
            @saved="onUpdateMatchSettingSaved"
            @error="onUpdateMatchSettingError"
        />

        <modalPushNotifLiveblog
            ref="modalPushNotifLiveblog"
            :match-id="selectedMatchId"
            :match="selectedMatch"
            :push-notif-enabled="pushNotifEnabled"
            @saved="onPushNotifSettingSaved"
            @error="onPushNotifSettingError"
        />

        <div v-if="!selectedMatchId" class="row mb-3">
            <div class="col-12">
                <div class="card">
                    <div class="card-body text-center py-5 text-muted">
                        Select a match above to open the liveblog workspace.
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="row liveblog-workspace mb-3">
            <div class="col-lg-6 mb-3 mb-lg-0">
                <div class="card liveblog-workspace__panel h-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <span class="text-muted small">Newest first</span>
                            <button
                                type="button"
                                class="btn btn-outline-secondary btn-sm"
                                @click="toggleOrder"
                            >
                                Order: {{ orderDirection }}
                            </button>
                        </div>
                        <div class="mb-3">
                            <b-form-input
                                v-model="filter"
                                type="search"
                                placeholder="Search timeline..."
                                size="sm"
                            />
                        </div>
                        <liveblogTimeline
                            :items="tableData"
                            :event-types="eventTypes"
                            :busy="isBusy || reorderBusy"
                            :reorder-busy="reorderBusy"
                            :can-reorder="canReorderLiveblog"
                            :filter="filter"
                            :active-event-id="editingEventId"
                            :order-direction="orderDirection"
                            @edit="onEditEntry"
                            @delete="removeLiveblog"
                            @reorder="onTimelineReorder"
                            @reorder-cancel="onTimelineReorderCancel"
                        />
                    </div>
                </div>
            </div>
            <div class="col-lg-6">
                <div class="card liveblog-workspace__panel h-100">
                    <div class="card-body liveblog-workspace__composer">
                        <liveblogComposer
                            ref="composer"
                            :match-id="selectedMatchId"
                            :match="selectedMatch"
                            :event-types="eventTypes"
                            :event-reasons="eventReasons"
                            :periods="periods"
                            :match-lineups="matchLineups"
                            :entries="tableData"
                            @saved="onComposerSaved"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="selectedMatchId" class="row">
            <div class="col-12">
                <div class="card">
                    <div
                        class="card-header d-flex justify-content-between align-items-center py-2"
                        role="button"
                        @click="showImportSection = !showImportSection"
                    >
                        <span class="fw-semibold">
                            Import from match events ({{ matchEvents.length }})
                        </span>
                        <i :class="showImportSection ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
                    </div>
                    <b-collapse v-model="showImportSection">
                        <div class="card-body">
                            <p class="text-muted small mb-2">
                                Optional: prefill the composer from the sport data feed.
                            </p>
                            <div class="row mb-2">
                                <div class="col-sm-12 col-md-6 offset-md-6">
                                    <div class="dataTables_filter text-md-end">
                                        <label class="d-inline-flex align-items-center">
                                            Search:
                                            <b-form-input
                                                v-model="eventsFilter"
                                                type="search"
                                                placeholder="Search..."
                                                class="form-control form-control-sm ms-2"
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div class="liveblog-events-scroll table-responsive mb-0">
                                <b-table
                                    :busy="eventsBusy"
                                    :items="matchEvents"
                                    :fields="eventFields"
                                    responsive="sm"
                                    :per-page="matchEvents.length || 1"
                                    :current-page="1"
                                    :filter="eventsFilter"
                                    small
                                    fixed
                                    show-empty
                                    empty-text="No match events found."
                                >
                                    <template #cell(player_name)="data">
                                        <span>{{ data.item.player_name || '—' }}</span>
                                        <span v-if="data.item.related_player_name" class="text-muted small d-block">
                                            {{ data.item.related_player_name }}
                                        </span>
                                    </template>
                                    <template #cell(score)="data">
                                        <span v-if="data.item.home_goals !== null && data.item.home_goals !== ''">
                                            {{ data.item.home_goals }} - {{ data.item.away_goals }}
                                        </span>
                                        <span v-else>—</span>
                                    </template>
                                    <template #cell(place)="data">
                                        <span v-if="data.item.place" class="text-capitalize">{{ data.item.place }}</span>
                                        <span v-else>—</span>
                                    </template>
                                    <template #cell(import_status)="data">
                                        <span v-if="isEventImported(data.item.event_id)" class="badge bg-success">Imported</span>
                                        <span v-else class="badge bg-secondary">Not imported</span>
                                    </template>
                                    <template #cell(action)="data">
                                        <button
                                            v-if="!isEventImported(data.item.event_id)"
                                            type="button"
                                            class="btn btn-sm btn-primary"
                                            title="Prefill composer from this event"
                                            @click="addLiveblogFromEvent(data.item)"
                                        >
                                            <i class="mdi mdi-plus me-1"></i>Use
                                        </button>
                                        <span v-else class="text-muted">—</span>
                                    </template>
                                    <template #table-busy>
                                        <div class="text-center text-danger my-2">
                                            <b-spinner class="align-middle"></b-spinner>
                                            <strong>Loading events...</strong>
                                        </div>
                                    </template>
                                </b-table>
                            </div>
                        </div>
                    </b-collapse>
                </div>
            </div>
        </div>
    </Layout>
</template>

<style scoped>
.liveblog-workspace__panel {
    min-height: calc(100vh - 220px);
}
.liveblog-workspace__composer {
    overflow-y: auto;
    max-height: calc(100vh - 240px);
    padding-bottom: 8px;
}
.liveblog-events-scroll {
    max-height: 420px;
    overflow-y: auto;
}
.liveblog-events-scroll >>> thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #fff;
    box-shadow: 0 1px 0 #dee2e6;
}
.timeline-settings-panel {
    min-height: 38px;
}
.timeline-settings-panel--with-checkboxes {
    min-height: 7rem;
}
.timeline-settings-panel-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}
.timeline-settings-panel-content--dimmed {
    opacity: 0.45;
    pointer-events: none;
}
.timeline-mode-control {
    min-height: 38px;
    display: flex;
    justify-content: center;
    width: 100%;
}
.timeline-mode-loader {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 0.25rem;
}
.timeline-mode-loader-text {
    font-size: 0.875rem;
    color: #6c757d;
}
.timeline-mode-buttons {
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
}
.timeline-mode-btn {
    min-width: 7.5rem;
    flex: 0 0 auto;
}
.timeline-mode-btn--second {
    margin-left: 1.25rem !important;
}
.timeline-options-checkboxes {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.65rem;
    text-align: left;
}
.timeline-options-checkboxes >>> .timeline-option-checkbox {
    width: 100%;
    margin-bottom: 0;
}
.timeline-options-checkboxes >>> .custom-control {
    position: relative;
    display: block;
    min-height: 1.5rem;
    padding-left: 3.5rem;
}
.timeline-options-checkboxes >>> .custom-control-label {
    line-height: 1.5rem;
    text-align: left;
    padding-left: 0.5rem;
}
.timeline-options-checkboxes >>> .custom-switch .custom-control-label::before {
    left: -3.5rem;
}
.timeline-options-checkboxes >>> .custom-switch .custom-control-label::after {
    left: calc(-3.5rem + 2px);
}
.timeline-options-checkboxes >>> .form-check {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-height: 1.5rem;
    padding-left: 0;
    margin-bottom: 0;
}
.timeline-options-checkboxes >>> .form-check-input {
    flex-shrink: 0;
    margin-top: 0;
    margin-left: 0;
    float: none;
}
.timeline-options-checkboxes >>> .form-check-label {
    line-height: 1.5rem;
    text-align: left;
    margin-left: 0.25rem;
}
</style>
